import React, {
  useMemo, useState, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import * as MuiIcons from '@material-ui/icons';
import { useTranslation } from 'utils/modifiedTranslation';
import { ReactComponent as DiagnosticIcon } from 'assets/icons/fluent_pulse.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search_rounded.svg';
import { ReactComponent as TreatmentIcon } from 'assets/icons/maki_doctor.svg';
import { ReactComponent as RecoveryIcon } from 'assets/icons/mdi_heart.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import { ReactComponent as CircleIcon } from 'assets/icons/circle_subtract.svg';
import { ReactComponent as ArrowLeft } from 'assets/icons/arrow_left_2.svg';
import { Preloader } from 'components/Preloader';
import app from 'new-ui/app';
import { GA_NBA_CATEGORY, GA_NBA_EVENTS } from 'modules/ga/generalEvents';
import { noop } from 'functions/noop';
import ActionCard from '../ActionCard/ActionCard';
import { getVisibleActions } from '../nba.utils';
import { ACTION_IDENTIFIER_RENDER_COMPONENT, DISPLAY_CONTEXTS, ACTION_TRANSITION_DIRECTION } from '../nba.constants';
import styles from './StepJourenyCard.module.css';

const ICON_PREFIX_MUI = 'mui';
const ICON_PREFIX_CUSTOM = 'custom';

const CUSTOM_ICON_REGISTRY = {
  Heart: RecoveryIcon,
  Doctor: TreatmentIcon,
  SearchRounded: SearchIcon,
  Pulse: DiagnosticIcon,
};

const ACTIONS_PER_PAGE = 1;
const MAX_VISIBLE_SUB_STEPS = 3;
const CAROUSEL_INTERVAL = 5000;

const getComponentToRender = ({
  dynamicActionComponent,
  actionStartIndex,
  totalActions,
  stepsOfActiveMilestone,
  selectedStepId,
  handleNextActionPage,
  onStepClick,
}) => {
  if (!dynamicActionComponent) {
    return null;
  }

  const canAdvanceToAction = actionStartIndex + ACTIONS_PER_PAGE < totalActions;
  const currentStepIndex = stepsOfActiveMilestone.findIndex((step) => step.name === selectedStepId);
  const canAdvanceToStep = currentStepIndex !== -1 && currentStepIndex < stepsOfActiveMilestone.length - 1;

  if (!(canAdvanceToAction || canAdvanceToStep)) {
    return dynamicActionComponent;
  }

  const onCompleteAndAdvance = () => {
    if (canAdvanceToAction) {
      handleNextActionPage();
      return;
    }

    const nextStep = stepsOfActiveMilestone[currentStepIndex + 1];
    if (nextStep && nextStep.name) {
      onStepClick(nextStep.name);
    }
  };

  return {
    ...dynamicActionComponent,
    props: {
      ...dynamicActionComponent.props,
      onCompleteAndAdvance,
    },
  };
};

const StepJourneyCard = ({
  milestones = [],
  activeMilestoneId = null,
  selectedStepId = null,
  completedMilestoneIds = [],
  onMilestoneClick = noop,
  onStepClick = noop,
  onActionClick = noop,
  isActionLoading = false,
  dynamicActionComponent = null,
  onPagedActionChange = noop,
  evaluationContext = {},
  displayContext = null,
}) => {
  const { t } = useTranslation();
  const [currentActionPage, setCurrentActionPage] = useState(1);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const [subStepViewIndex, setSubStepViewIndex] = useState(0);
  const [actionTransitionDirection, setActionTransitionDirection] = useState(ACTION_TRANSITION_DIRECTION.RIGHT);
  const [isManualActionPageChange, setIsManualActionPageChange] = useState(false);

  const getMilestoneIcon = (milestone) => {
    if (!milestone) {
      return <DiagnosticIcon />;
    }

    const { iconId } = milestone;

    if (typeof iconId === 'string' && iconId.length > 0) {
      const [prefix, rawIconName] = iconId.split(':');
      const iconName = rawIconName || '';

      if (prefix === ICON_PREFIX_MUI && iconName) {
        const MuiIconComponent = MuiIcons[iconName];
        if (MuiIconComponent) {
          return <MuiIconComponent />;
        }
      }

      if (prefix === ICON_PREFIX_CUSTOM && iconName) {
        const CustomIconComponent = CUSTOM_ICON_REGISTRY[iconName];
        if (CustomIconComponent) {
          return <CustomIconComponent />;
        }
      }
    }

    return <DiagnosticIcon />;
  };

  const getStepIcon = (stepName) => (selectedStepId === stepName
    ? <CheckIcon className={styles.subStepSelectedIcon} />
    : <CircleIcon className={styles.subStepIcon} />);

  const sortedMilestones = useMemo(() => {
    if (!Array.isArray(milestones)) {
      return [];
    }

    return milestones
      .map((milestone, index) => ({
        ...milestone,
        order: typeof milestone.order === 'number' ? milestone.order : index + 1,
      }))
      .sort((a, b) => a.order - b.order);
  }, [milestones]);

  const activeMilestone = useMemo(() => (
    sortedMilestones.find((m) => m.name === activeMilestoneId)
  ), [sortedMilestones, activeMilestoneId]);

  const stepsOfActiveMilestone = useMemo(() => (activeMilestone?.steps ?? [])
    .map((step, index) => ({
      ...step,
      order: typeof step.order === 'number' ? step.order : index + 1,
    }))
    .sort((a, b) => a.order - b.order), [activeMilestone]);

  useEffect(() => {
    setSubStepViewIndex(0);
  }, [activeMilestoneId]);

  const totalSubSteps = stepsOfActiveMilestone.length;
  const canNavigateSubSteps = totalSubSteps > MAX_VISIBLE_SUB_STEPS;

  const currentVisibleSubSteps = useMemo(() => {
    if (!canNavigateSubSteps) return stepsOfActiveMilestone;
    return stepsOfActiveMilestone.slice(subStepViewIndex, subStepViewIndex + MAX_VISIBLE_SUB_STEPS);
  }, [stepsOfActiveMilestone, subStepViewIndex, canNavigateSubSteps]);

  const showPrevSubStepArrow = canNavigateSubSteps && subStepViewIndex > 0;
  const showNextSubStepArrow = canNavigateSubSteps && subStepViewIndex < totalSubSteps - MAX_VISIBLE_SUB_STEPS;

  const handlePrevSubStepView = useCallback(
    () => setSubStepViewIndex((prev) => Math.max(0, prev - 1)),
    [],
  );

  const handleNextSubStepView = useCallback(
    () => setSubStepViewIndex(
      (prev) => Math.min(
        totalSubSteps - MAX_VISIBLE_SUB_STEPS,
        prev + 1,
      ),
    ),
    [totalSubSteps],
  );

  const currentStepData = useMemo(() => (
    stepsOfActiveMilestone.find((s) => s.name === selectedStepId)
  ), [stepsOfActiveMilestone, selectedStepId]);

  const actionsOfCurrentStep = useMemo(() => (
    getVisibleActions(currentStepData?.actions ?? [], evaluationContext, displayContext)
  ), [currentStepData, evaluationContext, displayContext]);

  useEffect(() => {
    setCurrentActionPage(1);
    setIsCarouselPaused(false);
    setIsManualActionPageChange(false);
  }, [selectedStepId, actionsOfCurrentStep]);

  const totalActions = actionsOfCurrentStep.length;
  const actionStartIndex = (currentActionPage - 1) * ACTIONS_PER_PAGE;
  const displayedAction = actionsOfCurrentStep[actionStartIndex];

  useEffect(() => {
    if (
      displayedAction
      && displayedAction.onActionCallIdentifier !== ACTION_IDENTIFIER_RENDER_COMPONENT
      && isManualActionPageChange
    ) {
      onPagedActionChange(displayedAction.name);
      setIsManualActionPageChange(false);
    } else if (totalActions === 0 && currentActionPage === 1) {
      onPagedActionChange(null);
    }
  }, [displayedAction, onPagedActionChange, totalActions, currentActionPage, isManualActionPageChange]);

  useEffect(() => {
    if (
      displayedAction?.onActionCallIdentifier === ACTION_IDENTIFIER_RENDER_COMPONENT
      && !isActionLoading
      && (!dynamicActionComponent || dynamicActionComponent.originatingActionName !== displayedAction.name)
    ) {
      onActionClick(displayedAction);
    }
  }, [displayedAction, onActionClick, isActionLoading, dynamicActionComponent]);

  useEffect(() => {
    if (isCarouselPaused || totalActions <= 1) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setActionTransitionDirection(ACTION_TRANSITION_DIRECTION.RIGHT);
      setIsManualActionPageChange(false);
      setCurrentActionPage((prev) => {
        const maxPage = Math.ceil(totalActions / ACTIONS_PER_PAGE);
        if (prev >= maxPage) {
          return 1;
        }

        return prev + 1;
      });
    }, CAROUSEL_INTERVAL);

    return () => clearTimeout(timer);
  }, [currentActionPage, totalActions, isCarouselPaused]);

  const handlePrevActionPage = useCallback(() => {
    setIsCarouselPaused(true);
    setIsManualActionPageChange(true);
    app.sendGoogleAnalyticsEvent(
      GA_NBA_CATEGORY,
      GA_NBA_EVENTS.STEP_NAVIGATOR_CLICKED,
      { direction: 'previous' },
    );
    setActionTransitionDirection(ACTION_TRANSITION_DIRECTION.LEFT);
    setCurrentActionPage((prev) => Math.max(1, prev - 1));
  }, []);

  const handleNextActionPage = useCallback(() => {
    setIsCarouselPaused(true);
    setIsManualActionPageChange(true);
    app.sendGoogleAnalyticsEvent(
      GA_NBA_CATEGORY,
      GA_NBA_EVENTS.STEP_NAVIGATOR_CLICKED,
      { direction: 'next' },
    );
    setActionTransitionDirection(ACTION_TRANSITION_DIRECTION.RIGHT);
    setCurrentActionPage((prev) => Math.min(Math.ceil(totalActions / ACTIONS_PER_PAGE), prev + 1));
  }, [totalActions]);

  const handleActionExecution = useCallback(async (actionToExecute) => {
    setIsCarouselPaused(true);
    await onActionClick(actionToExecute);
    if (actionToExecute.autoAdvance === false) {
      return;
    }
    const isLastActionInStep = !(actionStartIndex + ACTIONS_PER_PAGE < totalActions);

    if (isLastActionInStep) {
      const currentStepIndex = stepsOfActiveMilestone.findIndex((step) => step.name === selectedStepId);

      if (currentStepIndex !== -1 && currentStepIndex < stepsOfActiveMilestone.length - 1) {
        const nextStep = stepsOfActiveMilestone[currentStepIndex + 1];
        if (nextStep && nextStep.name) {
          onStepClick(nextStep.name);
        }
      }
    } else {
      handleNextActionPage();
    }
  }, [
    onActionClick,
    handleNextActionPage,
    onStepClick,
    totalActions,
    actionStartIndex,
    stepsOfActiveMilestone,
    selectedStepId,
  ]);

  const renderActionArea = () => {
    if (isActionLoading) {
      return (
        <div className={styles.actionLoadingPlaceholder}>
          <Preloader isVisible inline />
        </div>
      );
    }

    if (!displayedAction) {
      return null;
    }

    const componentToRender = getComponentToRender({
      dynamicActionComponent,
      actionStartIndex,
      totalActions,
      stepsOfActiveMilestone,
      selectedStepId,
      handleNextActionPage,
      onStepClick,
    });

    return (
      <ActionCard
        key={`${activeMilestoneId}_${selectedStepId}_action_${displayedAction.name}_${actionStartIndex}`}
        title={displayedAction.title || 'What to do next?'}
        buttonText={displayedAction.name}
        buttonAction={() => handleActionExecution(displayedAction)}
        description={displayedAction.description || `Complete the action: ${displayedAction.name}`}
        linkText={displayedAction.linkText || 'Need our help?'}
        actionName={displayedAction.name}
        showPrev={currentActionPage > 1}
        showNext={actionStartIndex + ACTIONS_PER_PAGE < totalActions}
        onPrev={handlePrevActionPage}
        onNext={handleNextActionPage}
        totalSteps={totalActions}
        currentStep={currentActionPage}
        transitionDirection={actionTransitionDirection}
        dynamicContentComponent={componentToRender}
        isDynamicContentSource={dynamicActionComponent?.originatingActionName === displayedAction.name
          && displayedAction?.onActionCallIdentifier === ACTION_IDENTIFIER_RENDER_COMPONENT}
      />
    );
  };

  const handleManualInteraction = useCallback(() => {
    setIsCarouselPaused(true);
  }, []);

  return (
    <div>
      <div className={styles.stepJourneyCard}>
        {sortedMilestones.length > 0 ? (
          <div className={styles.stepRow}>
            {sortedMilestones.map((milestone, index) => {
              if (!milestone || typeof milestone.name !== 'string') return null;
              const isActive = milestone.name === activeMilestoneId;
              const isCompleted = completedMilestoneIds.includes(milestone.name);
              const isDisabled = milestone.isDisabled === true;
              return (
                <React.Fragment key={milestone.name}>
                  <button
                    type="button"
                    className={
                      cx(styles.step, { [styles.activeStep]: isActive, [styles.completedStep]: isCompleted })
                    }
                    onClick={() => !isDisabled && onMilestoneClick(milestone.name)}
                    disabled={isDisabled}
                    aria-current={isActive ? 'step' : undefined}
                  >
                    <div className={styles.stepIconWrapper}>{getMilestoneIcon(milestone)}</div>
                    <span className={styles.stepLabel}>{milestone.name}</span>
                  </button>
                  {index < sortedMilestones.length - 1 && <div className={styles.connector} />}
                </React.Fragment>
              );
            })}
          </div>
        ) : null}

        {Array.isArray(stepsOfActiveMilestone) && stepsOfActiveMilestone.length > 0 && (
          <div className={styles.subStepNavContainer}>
            {showPrevSubStepArrow && (
              <button
                type="button"
                className={cx(styles.subStepArrow, styles.subStepArrowPrev)}
                onClick={handlePrevSubStepView}
                aria-label={t('nba.previous_sub_steps')}
                disabled={!showPrevSubStepArrow}
              >
                <ArrowLeft />
              </button>
            )}
            <div className={styles.subStepRow}>
              {(canNavigateSubSteps ? currentVisibleSubSteps : stepsOfActiveMilestone).map((step, index, arr) => {
                if (!step || typeof step.name !== 'string') return null;
                const isSelected = step.name === selectedStepId;
                return (
                  <React.Fragment key={step.name}>
                    <button
                      type="button"
                      className={cx(styles.subStep, { [styles.selectedSubStep]: isSelected })}
                      onClick={() => onStepClick(step.name)}
                      aria-pressed={isSelected}
                    >
                      <div className={styles.subStepIconWrapper}>{getStepIcon(step.name)}</div>
                      <span className={styles.subStepLabel}>{step.name}</span>
                    </button>
                    {index < arr.length - 1 && <div className={styles.subConnector} />}
                  </React.Fragment>
                );
              })}
            </div>
            {showNextSubStepArrow && (
              <button
                type="button"
                className={cx(styles.subStepArrow, styles.subStepArrowNext)}
                onClick={handleNextSubStepView}
                aria-label={t('nba.next_sub_steps')}
                disabled={!showNextSubStepArrow}
              >
                <ArrowLeft
                  style={{ transform: 'rotate(180deg)' }}
                />
              </button>
            )}
          </div>
        )}
      </div>
      <div
        className={styles.actionsContainer}
        onClickCapture={handleManualInteraction}
      >
        {renderActionArea()}
      </div>
    </div>
  );
};

StepJourneyCard.propTypes = {
  milestones: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    order: PropTypes.number,
    iconId: PropTypes.string,
    steps: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      order: PropTypes.number,
      actions: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        order: PropTypes.number,
        title: PropTypes.string,
        description: PropTypes.string,
        linkText: PropTypes.string,
        onActionCallIdentifier: PropTypes.string,
        args: PropTypes.object,
        renderAnotherComponent: PropTypes.string,
        renderAnotherComponentArgs: PropTypes.object,
        conditions: PropTypes.object,
        autoAdvance: PropTypes.bool,
      })),
    })),
  })),
  activeMilestoneId: PropTypes.string,
  selectedStepId: PropTypes.string,
  completedMilestoneIds: PropTypes.arrayOf(PropTypes.string),
  onMilestoneClick: PropTypes.func,
  onStepClick: PropTypes.func,
  onActionClick: PropTypes.func,
  isActionLoading: PropTypes.bool,
  dynamicActionComponent: PropTypes.shape({
    Component: PropTypes.elementType,
    props: PropTypes.object,
    originatingActionName: PropTypes.string,
  }),
  onPagedActionChange: PropTypes.func,
  evaluationContext: PropTypes.object,
  displayContext: PropTypes.oneOf(DISPLAY_CONTEXTS),
};

export default StepJourneyCard;
